import React, { useMemo, useState } from 'react';
import { useI18n } from '../contexts/I18nContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { formatDateTime } from '../lib/utils';
import { classifyEventCategory } from '../lib/eventCategory';
import { Plus, Trash2, Calendar, Landmark, Truck, Warehouse, ListFilter } from 'lucide-react';

// Re-exported so callers and existing tests can still import from EventsPanel.
export { classifyEventCategory };

const FILTER_CHIPS = [
  { key: 'all', icon: ListFilter, color: 'border-border text-foreground', active: 'bg-foreground/10 border-foreground' },
  { key: 'aduana', icon: Landmark, color: 'border-emerald-500/40 text-emerald-300', active: 'bg-emerald-500/20 border-emerald-400 text-emerald-200' },
  { key: 'transporte', icon: Truck, color: 'border-sky-500/40 text-sky-300', active: 'bg-sky-500/20 border-sky-400 text-sky-200' },
  { key: 'almacen', icon: Warehouse, color: 'border-amber-500/40 text-amber-300', active: 'bg-amber-500/20 border-amber-400 text-amber-200' },
];

export function EventsPanel({
  events,
  eventTypes,
  containers,
  newEvent,
  setNewEvent,
  onAdd,
  onDelete,
  isReadOnly,
}) {
  const { t } = useI18n();
  const [activeFilter, setActiveFilter] = useState('all');

  // Pre-compute counts per category so the chips show how many events match.
  const counts = useMemo(() => {
    const acc = { all: events.length, aduana: 0, transporte: 0, almacen: 0, otros: 0 };
    for (const ev of events) {
      const cat = classifyEventCategory(ev.event_type_code);
      acc[cat] = (acc[cat] || 0) + 1;
    }
    return acc;
  }, [events]);

  const filteredEvents = useMemo(() => {
    if (activeFilter === 'all') return events;
    return events.filter((ev) => classifyEventCategory(ev.event_type_code) === activeFilter);
  }, [events, activeFilter]);
  return (
    <>
      {!isReadOnly && (
        <div className="bg-card border border-border rounded-sm p-4 space-y-4">
          <h3 className="font-heading text-lg">{t('shipments.newEvent')}</h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <Label>{t('shipments.eventType')}</Label>
              <Select
                value={newEvent.event_type_code}
                onValueChange={(v) => setNewEvent({ ...newEvent, event_type_code: v })}
              >
                <SelectTrigger className="mt-1" data-testid="event-type-select">
                  <SelectValue placeholder={t('shipments.selectPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {eventTypes.map((e) => (
                    <SelectItem key={e.code} value={e.code}>{e.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('shipments.eventDate')}</Label>
              <Input
                type="datetime-local"
                value={newEvent.event_date}
                onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
                className="mt-1"
                data-testid="event-date-input"
              />
            </div>
            <div>
              <Label>{t('shipments.containerOptional')}</Label>
              <Select
                value={newEvent.container_number}
                onValueChange={(v) => setNewEvent({ ...newEvent, container_number: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('shipments.blLevel')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bl">{t('shipments.blLevel')}</SelectItem>
                  {containers.map((c) => (
                    <SelectItem key={c.container_number} value={c.container_number}>
                      {c.container_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex items-center gap-2">
                <Switch
                  checked={newEvent.visible_to_client}
                  onCheckedChange={(v) => setNewEvent({ ...newEvent, visible_to_client: v })}
                />
                <Label className="text-xs">{t('shipments.visibleToClient')}</Label>
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <Label>{t('shipments.noteOptional')}</Label>
              <Input
                value={newEvent.note}
                onChange={(e) => setNewEvent({ ...newEvent, note: e.target.value })}
                placeholder={t('shipments.eventNotePlaceholder')}
                className="mt-1"
                data-testid="event-note-input"
              />
            </div>
            <Button onClick={onAdd} className="self-end" data-testid="add-event-btn">
              <Plus size={16} className="mr-1" />
              {t('shipments.addEvent')}
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="event-filter-chips">
        {FILTER_CHIPS.map((chip) => {
          const count = counts[chip.key] ?? 0;
          const isActive = activeFilter === chip.key;
          const Icon = chip.icon;
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => setActiveFilter(chip.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                isActive ? chip.active : `${chip.color} bg-transparent hover:bg-foreground/5`
              }`}
              data-testid={`event-filter-${chip.key}`}
            >
              <Icon size={13} />
              <span>{t(`shipments.eventFilter.${chip.key}`)}</span>
              <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] ${isActive ? 'bg-background/20' : 'bg-foreground/10'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="space-y-2">
        {filteredEvents.length === 0 ? (
          <div className="bg-card border border-border rounded-sm p-8 text-center" data-testid="events-empty-state">
            <Calendar className="mx-auto w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {activeFilter === 'all'
                ? t('shipments.noEventsRegistered')
                : t('shipments.noEventsInCategory')}
            </p>
          </div>
        ) : (
          filteredEvents.map((event) => {
            const category = classifyEventCategory(event.event_type_code);
            const dotColor =
              category === 'aduana' ? 'bg-emerald-400'
              : category === 'transporte' ? 'bg-sky-400'
              : category === 'almacen' ? 'bg-amber-400'
              : 'bg-primary';
            return (
            <div
              key={event.id}
              className="bg-card border border-border rounded-sm p-4 flex items-start justify-between"
              data-testid={`event-${event.id}`}
              data-event-category={category}
            >
              <div className="flex gap-4">
                <div className={`w-2 h-2 mt-2 rounded-full ${dotColor}`} />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{event.event_type_label || event.event_type_code}</span>
                    {event.container_number && (
                      <span className="text-xs bg-secondary px-2 py-0.5 rounded font-mono">
                        {event.container_number}
                      </span>
                    )}
                    {!event.visible_to_client && (
                      <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                        {t('shipments.internalOnly')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{formatDateTime(event.event_date)}</p>
                  {event.note && <p className="text-sm mt-2">{event.note}</p>}
                </div>
              </div>
              {!isReadOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(event.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
            );
          })
        )}
      </div>
    </>
  );
}
